import React, { useEffect, useState } from 'react';
import { gettext } from '@eventbrite/i18n';
import { setWindowLocation } from '@eventbrite/http';

import { LinkChunky } from '@eventbrite/eds-iconography';
import { AlertChunky } from '@eventbrite/eds-iconography';

import PasswordlessForm from './newForms/PasswordlessForm';
import LinkedAccountsGreyDialog from './LinkedAccountsGrayDialog';
import PasswordlessEmailStatus from './PasswordlessEmailStatus';
import {
    BASE_URL,
    PASSWORDLESS_LOGIN_OK,
    PASSWORDLESS_LOGIN_NOT_AUTHORIZED,
    PASSWORDLESS_LOGIN_TOKEN_EXPIRED,
    PASSWORDLESS_LOGIN_TOKEN_REUSED,
    PASSWORDLESS_LOGIN_OTHER_ERROR,
    PASSWORDLESS_LOGIN_USER_ACCOUNT_CLOSED,
} from '../constants';
import {
    TEXT_TOKEN_USED_ONCE,
    TEXT_TOKEN_EXPIRES_TIME,
} from '../constants/messages';

import { PASSWORDLESS_LOGIN_MUST_ACCEPT_TOS } from '../constants';
import { PASSWORDLESS_EMAIL_STATUSES } from '../constants/status';

const getStrings = ({ status, isLoading }) => {
    let iconType = null;
    let headerText = gettext('Please wait while we log you in');
    let subtitleTextDark = gettext('We are procesing your request.');
    let subtitleText = '';
    let buttonText = '';
    let buttonRedirection = null;
    if (!isLoading) {
        iconType = <LinkChunky />;
        headerText = gettext('Your are logged in!');
        subtitleTextDark = gettext('You will be redirected to Home.');
        subtitleText = '';

        buttonText = gettext('Continue');
        buttonRedirection = '/';
    }

    // For the time beign all the texts for the failed status are placeholders, once we have the official copies these should be changed
    if (status === PASSWORDLESS_LOGIN_OTHER_ERROR) {
        iconType = <AlertChunky />;
        headerText = gettext('Something went wrong trying to log in!');
        subtitleTextDark = gettext('Please try again in a few minutes');
        subtitleText = gettext(
            'If the issue persists please contact technical support',
        );
        buttonText = gettext('Close');
        buttonRedirection = BASE_URL;
    } else if (status === PASSWORDLESS_LOGIN_TOKEN_EXPIRED) {
        iconType = <AlertChunky />;
        headerText = gettext('This link has expired');
        subtitleTextDark = gettext('We can send a new link to');
        subtitleText = TEXT_TOKEN_USED_ONCE;
        buttonText = gettext('Close');
        buttonRedirection = BASE_URL;
    } else if (status === PASSWORDLESS_LOGIN_TOKEN_REUSED) {
        iconType = <AlertChunky />;
        headerText = gettext('This link has already been used or expired');
        subtitleTextDark = gettext('We can send a new link to');
        subtitleText = TEXT_TOKEN_USED_ONCE;
        buttonText = gettext('Close');
        buttonRedirection = BASE_URL;
    } else if (status === PASSWORDLESS_LOGIN_MUST_ACCEPT_TOS) {
        iconType = <AlertChunky />;
        headerText = gettext('User must accept Terms and conditions!');
        subtitleTextDark = gettext(
            'To continue, you need to accept our Terms and Conditions.',
        );
        subtitleText = gettext('Please try again.');
        buttonText = gettext('Close');
        buttonRedirection = BASE_URL;
    } else if (
        status === PASSWORDLESS_LOGIN_NOT_AUTHORIZED ||
        status === PASSWORDLESS_LOGIN_USER_ACCOUNT_CLOSED
    ) {
        iconType = <AlertChunky />;
        headerText = gettext("We don't recognize your email address.");
        subtitleTextDark = gettext('Please try again, or create an account.');
        subtitleText = '';
        buttonText = gettext('Go to Log in');
        buttonRedirection = BASE_URL;
    }

    const strings = {
        iconType,
        headerText,
        subtitleTextDark,
        subtitleText,
        buttonText,
        buttonRedirection,
    };

    return strings;
};

const PasswordlessLoginMessage = ({
    status = PASSWORDLESS_LOGIN_OK,
    isLoading,
    handleSendPasswordlessLink,
    passwordlessEmailStatus,
    signUpSecondStep,
    referrer,
}) => {
    const {
        iconType,
        headerText,
        subtitleText,
        subtitleTextDark,
        buttonText,
        buttonRedirection,
    } = getStrings({ status, isLoading });

    const [passwordlessEmail, setPasswordlessEmail] = useState('');

    const _handleOnClick = () => {
        setWindowLocation(buttonRedirection);
    };

    useEffect(() => {
        if (
            !isLoading &&
            status !== PASSWORDLESS_LOGIN_TOKEN_REUSED &&
            status !== PASSWORDLESS_LOGIN_TOKEN_EXPIRED
        ) {
            const timer = setTimeout(() => _handleOnClick(), 5000);

            return function cleanup() {
                clearTimeout(timer);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading]);
    const _handlePasswordlessEmail = ({ email, isDirect = false }) => {
        setPasswordlessEmail(email);
        handleSendPasswordlessLink?.({ email, isDirect, ref: referrer });
    };

    if (
        !isLoading &&
        passwordlessEmailStatus === PASSWORDLESS_EMAIL_STATUSES.delivered
    ) {
        return (
            <PasswordlessEmailStatus
                email={passwordlessEmail}
                onClick={_handleOnClick}
            />
        );
    } else if (
        !isLoading &&
        (status === PASSWORDLESS_LOGIN_TOKEN_REUSED ||
            status === PASSWORDLESS_LOGIN_TOKEN_EXPIRED)
    ) {
        return (
            <PasswordlessForm
                iconType={iconType}
                headerText={headerText}
                subtitleText={subtitleTextDark}
                onSubmit={_handlePasswordlessEmail}
                dataTestId="resend-passwordless-form"
                isSubmitOngoing={
                    passwordlessEmailStatus ===
                    PASSWORDLESS_EMAIL_STATUSES.ongoing
                }
                onClickBackToLogin={_handleOnClick}
                signUpSecondStep={signUpSecondStep}
                tokenExpiredText={TEXT_TOKEN_EXPIRES_TIME}
                backText={gettext('Go to Log In')}
                showNotification={false}
                autoFocus={true}
                buttonCTA={gettext('Resend Link')}
            />
        );
    } else {
        return (
            <LinkedAccountsGreyDialog
                iconType={iconType}
                headerText={headerText}
                subtitleTextDark={subtitleTextDark}
                subtitleText={subtitleText}
                buttonText={buttonText}
                handleOnClick={_handleOnClick}
                isLoading={isLoading}
            />
        );
    }
};

export default PasswordlessLoginMessage;
